import { dateOnly, formatMonthDayDow, normalizeTime, todayKst } from '@indipia/shared-features';

// C-3-1 거래처 발주 회신 — 순수 로직·문구 상수 (화면 파일에서 분리, 시험 대상).
// 문구는 940 표현 우선, 940에 없는 것은 이 파일 상수 한 곳에 모음(기획 확정 시 상수만 교체).

// ── 서버 규격 (B1·B2) ───────────────────────────────────────

export type SendTypeCode = 'ORDER_REQUEST' | 'ORDER_CHANGE';
export type ReplyResult = 'ACCEPTED' | 'DECLINED';
export type YesNo = 'Y' | 'N';
export type DeclineReason = 'NO_STOCK' | 'NO_DEADLINE' | 'PRICE_CHANGE' | 'ETC';

export interface OrderItem {
  itemSeq: number;
  itemName: string;
  itemSpec: string | null;
  itemQty: number;
  itemUnit: string | null;
}

export interface OrderReply {
  replyResultCode: ReplyResult;
  replyDtm: string;
  replyReasonCode: DeclineReason | null;
  availDate: string | null;
  liftAvailYn: YesNo | null;
  replyNote: string | null;
}

/** B1 data — 서버 값 그대로(검증 전). */
export interface OrderShareData {
  state?: string;
  closedReasonCode?: string | null;
  sendTypeCode?: string;
  companyName?: string | null;
  companyRepName?: string | null;
  companyPhone?: string | null;
  requestedDtm?: string | null;
  siteLabel?: string | null;
  workTypeName?: string | null;
  deliveryAddress?: string | null;
  deliveryDate?: string;
  oldDeliveryDate?: string | null;
  items?: OrderItem[];
  liftReqYn?: string;
  orderNote?: string | null;
  reply?: OrderReply | null;
  expiresDtm?: string;
}

/** 검증을 통과한 화면용 값. */
export interface OrderView {
  sendTypeCode: SendTypeCode;
  companyName: string;
  /** 표시용 — 비어 있으면 줄 숨김 */
  companyRepName: string;
  companyPhone: string;
  /** 이 발주 요청·변경을 보낸 일시('YYYY-MM-DD'). 없거나 형식 이상이면 빈 문자열(줄 숨김) */
  requestedDate: string;
  requestedTime: string;
  siteLabel: string;
  workTypeName: string;
  deliveryAddress: string;
  deliveryDate: string;
  oldDeliveryDate: string;
  items: OrderItem[];
  liftReqYn: YesNo;
  orderNote: string;
  expiresDate: string;
  reply: OrderReply | null;
}

export interface ReplyPayload {
  replyResultCode: ReplyResult;
  liftAvailYn?: YesNo;
  replyReasonCode?: DeclineReason;
  availDate?: string;
  replyNote?: string;
}

export interface ReplyResponse {
  state?: string;
  replyResultCode?: string;
  replyDtm?: string;
}

// 외부 링크에서는 공통코드 API 를 부를 수 없어 FE 상수로 둠. 서버 코드(ZEU_ORDER_DECLINE_REASON)와 글자 일치 필요.
export const DECLINE_REASONS: readonly { code: DeclineReason; label: string }[] = [
  { code: 'NO_STOCK', label: '재고 부족' },
  { code: 'NO_DEADLINE', label: '납기 불가' },
  { code: 'PRICE_CHANGE', label: '단가 변동' },
  { code: 'ETC', label: '기타' },
];

export const NOTE_MAX_LENGTH = 100;

// ── 문구 ────────────────────────────────────────────────────

export const TEXT = {
  documentTitle: '발주 확인 · 회신',
  heading: '발주 확인 · 회신',
  subtitleChange: '발주 변경 요청',
  subtitleRequest: (companyName: string) => `${companyName} 발주 요청`,
  linkNotice: (expiresLabel: string) => `링크는 ${expiresLabel}까지 열 수 있어요.`,
  oldDeliveryDate: (label: string) => `변경 전 ${label}`,
  liftRequested: '요청',
  liftNotRequested: '요청 없음',
  availQuestion: '입고 가능하신가요?',
  accept: '가능',
  decline: '불가',
  liftAvailQuestion: '양중 가능 여부',
  reasonQuestion: '불가 사유',
  availDateLabel: '가능한 날짜',
  noteLabel: '한 줄 메모',
  notePlaceholder: '예) 엘보 입고가 10/13이라 하루 뒤 가능',
  required: '필수',
  optional: '(선택)',
  formNotice: '회신하면 현장 담당자·관리자에게 바로 알림이 가요. 링크는 입고일 다음날까지 열 수 있어요.',
  submitPickResult: '가능 / 불가를 골라 주세요',
  submitPickLift: '양중 가능 여부를 골라 주세요',
  submitPickReason: '불가 사유를 골라 주세요',
  submitReadyAccept: '가능으로 회신 보내기',
  submitReadyDecline: '불가로 회신 보내기',
  availDatePast: '오늘 이후 날짜를 골라 주세요',
  noteTooLong: `메모는 ${NOTE_MAX_LENGTH}자 이내로 입력해 주세요.`,
  doneTitle: '회신을 보냈어요',
  doneNotice: '담당자에게 바로 알림이 가요.',
  repliedCardTitle: '보낸 회신',
  repliedNotice: '회신은 바꿀 수 없어요. 변동이 있으면 요청한 곳으로 연락해 주세요.',
  alreadyReplied: '이미 회신이 접수되어 있어요.',
  liftAvailable: '양중 가능',
  liftUnavailable: '양중 불가',
  resultAccepted: '가능',
  resultDeclined: '불가',
  fieldCheck: '입력한 내용을 확인해 주세요.',
  serverRetry: '잠시 후 다시 시도해 주세요.',
  networkNotSent: '회신을 보내지 못했어요. 네트워크를 확인하고 다시 눌러 주세요.',
  // 발주 내용 카드 라벨
  cardAriaLabel: '발주 내용',
  cardRep: '대표자',
  cardPhone: '연락처',
  cardRequestedDtm: '발주 요청',
  cardSite: '현장',
  backToList: '← 목록으로',
  headerOrderList: '☰ 받은 발주',
  orderListLink: '받은 발주 전체 보기',
  orderListOpening: '여는 중…',
  orderListFail: '받은 발주 목록을 열지 못했어요. 잠시 후 다시 눌러 주세요.',
  cardWorkType: '공종',
  cardDeliveryDate: '입고희망일',
  cardLift: '양중',
  cardAddress: '배송지',
  cardItems: '품목',
  cardNote: '남긴 말',
} as const;

// ── 서버 응답 검증 (필수 신호 부재는 폴백 없이 UNKNOWN) ───────

export type InspectResult =
  | { kind: 'OPEN'; view: OrderView }
  | { kind: 'REPLIED'; view: OrderView }
  | { kind: 'CLOSED' | 'EXPIRED' | 'CHANGED' | 'UNKNOWN' };

const REASON_CODES = new Set<string>(DECLINE_REASONS.map((r) => r.code));

function str(v: string | null | undefined): string {
  return typeof v === 'string' ? v.trim() : '';
}

function validReply(r: OrderReply | null | undefined): r is OrderReply {
  if (!r) return false;
  if (r.replyResultCode !== 'ACCEPTED' && r.replyResultCode !== 'DECLINED') return false;
  if (!r.replyDtm || r.replyDtm.length < 16) return false;
  try {
    dateOnly(r.replyDtm);
    normalizeTime(r.replyDtm.slice(11, 16));
  } catch {
    return false;
  }
  if (r.replyResultCode === 'DECLINED' && !(r.replyReasonCode && REASON_CODES.has(r.replyReasonCode))) return false;
  if (r.liftAvailYn != null && r.liftAvailYn !== 'Y' && r.liftAvailYn !== 'N') return false;
  if (r.availDate) {
    try {
      dateOnly(r.availDate);
    } catch {
      return false;
    }
  }
  return true;
}

/** 발주 요청 일시 — 표시용이라 없거나 형식이 이상하면 빈 값(줄 숨김, 다른 값으로 대체하지 않음). */
function parseRequestedDtm(v: string | null | undefined): { date: string; time: string } {
  if (!v || v.length < 16) return { date: '', time: '' };
  try {
    return { date: dateOnly(v), time: normalizeTime(v.slice(11, 16)) };
  } catch {
    return { date: '', time: '' };
  }
}

export function inspectOrderShare(d: OrderShareData | undefined | null): InspectResult {
  if (!d) return { kind: 'UNKNOWN' };
  if (d.state === 'CLOSED' || d.state === 'EXPIRED' || d.state === 'CHANGED') return { kind: d.state };
  if (d.state !== 'OPEN' && d.state !== 'REPLIED') return { kind: 'UNKNOWN' };
  if (d.sendTypeCode !== 'ORDER_REQUEST' && d.sendTypeCode !== 'ORDER_CHANGE') return { kind: 'UNKNOWN' };
  if (d.liftReqYn !== 'Y' && d.liftReqYn !== 'N') return { kind: 'UNKNOWN' };
  if (!Array.isArray(d.items) || d.items.length === 0) return { kind: 'UNKNOWN' };
  if (d.items.some((i) => !i || !str(i.itemName) || typeof i.itemQty !== 'number')) return { kind: 'UNKNOWN' };
  let deliveryDate: string;
  let oldDeliveryDate = '';
  let expiresDate = '';
  try {
    deliveryDate = dateOnly(d.deliveryDate ?? '');
    if (d.oldDeliveryDate) oldDeliveryDate = dateOnly(d.oldDeliveryDate);
    if (d.state === 'OPEN') expiresDate = dateOnly(d.expiresDtm ?? '');
  } catch {
    return { kind: 'UNKNOWN' };
  }
  let reply: OrderReply | null = null;
  if (d.state === 'REPLIED') {
    if (!validReply(d.reply)) return { kind: 'UNKNOWN' };
    reply = d.reply;
  }
  const requested = parseRequestedDtm(d.requestedDtm);
  return {
    kind: d.state,
    view: {
      sendTypeCode: d.sendTypeCode,
      companyName: str(d.companyName),
      companyRepName: str(d.companyRepName),
      companyPhone: str(d.companyPhone),
      requestedDate: requested.date,
      requestedTime: requested.time,
      siteLabel: str(d.siteLabel),
      workTypeName: str(d.workTypeName),
      deliveryAddress: str(d.deliveryAddress),
      deliveryDate,
      oldDeliveryDate,
      items: d.items,
      liftReqYn: d.liftReqYn,
      orderNote: str(d.orderNote),
      expiresDate,
      reply,
    },
  };
}

// ── 입력·검증·페이로드 (B2) ─────────────────────────────────

export interface ReplyDraft {
  result: ReplyResult | null;
  liftAvail: YesNo | null;
  reason: DeclineReason | null;
  availDate: string;
  note: string;
}

export const EMPTY_DRAFT: ReplyDraft = { result: null, liftAvail: null, reason: null, availDate: '', note: '' };

/** [가능]↔[불가]를 바꾸면 반대 갈래 입력을 비움(서버는 가능 회신에 사유·날짜가 오면 400). */
export function switchResult(draft: ReplyDraft, next: ReplyResult): ReplyDraft {
  if (draft.result === next) return draft;
  return { ...EMPTY_DRAFT, result: next };
}

export function submitGate(draft: ReplyDraft, liftReqYn: YesNo): { ready: boolean; label: string } {
  if (!draft.result) return { ready: false, label: TEXT.submitPickResult };
  if (draft.result === 'ACCEPTED' && liftReqYn === 'Y' && !draft.liftAvail) return { ready: false, label: TEXT.submitPickLift };
  if (draft.result === 'DECLINED' && !draft.reason) return { ready: false, label: TEXT.submitPickReason };
  return { ready: true, label: draft.result === 'ACCEPTED' ? TEXT.submitReadyAccept : TEXT.submitReadyDecline };
}

export interface ReplyFieldErrors {
  availDate?: string;
  replyNote?: string;
}

/** 클라이언트 사전 검사(서버가 최종 판정). today 는 서울 기준 'YYYY-MM-DD'. */
export function validateReply(draft: ReplyDraft, today: string = todayKst()): ReplyFieldErrors {
  const errors: ReplyFieldErrors = {};
  if (draft.result === 'DECLINED') {
    if (draft.availDate && draft.availDate < today) errors.availDate = TEXT.availDatePast;
    if (draft.note.trim().length > NOTE_MAX_LENGTH) errors.replyNote = TEXT.noteTooLong;
  }
  return errors;
}

/** 선택이 완료되지 않았으면 throw (필수 값 부재 폴백 금지). */
export function buildReplyPayload(draft: ReplyDraft, liftReqYn: YesNo): ReplyPayload {
  if (!draft.result) throw new Error('replyResultCode required');
  if (draft.result === 'ACCEPTED') {
    if (liftReqYn === 'Y') {
      if (!draft.liftAvail) throw new Error('liftAvailYn required');
      return { replyResultCode: 'ACCEPTED', liftAvailYn: draft.liftAvail };
    }
    return { replyResultCode: 'ACCEPTED' };
  }
  if (!draft.reason) throw new Error('replyReasonCode required');
  const payload: ReplyPayload = { replyResultCode: 'DECLINED', replyReasonCode: draft.reason };
  if (draft.availDate) payload.availDate = draft.availDate;
  const note = draft.note.trim();
  if (note) {
    if (note.length > NOTE_MAX_LENGTH) throw new Error('replyNote too long');
    payload.replyNote = note;
  }
  return payload;
}

// ── 표시 보조 ───────────────────────────────────────────────

export function declineReasonLabel(code: string | null | undefined): string {
  return DECLINE_REASONS.find((r) => r.code === code)?.label ?? '';
}

/** 'YYYY-MM-DDTHH:mm:ss' → 'MM/DD(요일) HH:mm' */
export function formatReplyDtm(dtm: string): string {
  const date = dateOnly(dtm);
  const hm = normalizeTime(dtm.slice(11, 16));
  return `${formatMonthDayDow(date)} ${hm}`;
}

/** 완료 화면 요약 한 줄. 가능 · 입고 MM/DD(요일) · 양중 가능 / 불가 · {사유} · 가능한 날짜 MM/DD(요일) */
export function replySummaryLine(reply: OrderReply, deliveryDate: string): string {
  if (reply.replyResultCode === 'ACCEPTED') {
    const parts: string[] = [TEXT.resultAccepted, `입고 ${formatMonthDayDow(deliveryDate)}`];
    if (reply.liftAvailYn) parts.push(reply.liftAvailYn === 'Y' ? TEXT.liftAvailable : TEXT.liftUnavailable);
    return parts.join(' · ');
  }
  const parts: string[] = [TEXT.resultDeclined, declineReasonLabel(reply.replyReasonCode)];
  if (reply.availDate) parts.push(`${TEXT.availDateLabel} ${formatMonthDayDow(dateOnly(reply.availDate))}`);
  return parts.join(' · ');
}

/** 발주 요청 일시 표시 'MM/DD(요일) HH:mm' — 값이 없으면 빈 문자열(줄 숨김). */
export function formatRequestedDtm(view: Pick<OrderView, 'requestedDate' | 'requestedTime'>): string {
  if (!view.requestedDate || !view.requestedTime) return '';
  return `${formatMonthDayDow(view.requestedDate)} ${view.requestedTime}`;
}

/** B2 400 의 field → 폼 필드 영역 */
export type ReplyFieldKey = 'replyResultCode' | 'replyReasonCode' | 'availDate' | 'liftAvailYn' | 'replyNote';
const FIELD_KEYS = new Set<string>(['replyResultCode', 'replyReasonCode', 'availDate', 'liftAvailYn', 'replyNote']);
export function asReplyFieldKey(field: string | undefined): ReplyFieldKey | null {
  return field && FIELD_KEYS.has(field) ? (field as ReplyFieldKey) : null;
}
